// A drawer that rises from the bottom of the screen on a phone, for what the
// wide screen shows as a small menu at a button (the post box's conditions, a
// file's "…"). The old web made one in the Agent chat's model picker and the
// person asked for it to be the one used everywhere (2026-09-30).
//
// Promise:
// - It lies over the box it is drawn in (`absolute inset-0`: the screen's
//   overlay host, or a viewer's own layer), never fixed (in a fixed box iOS
//   did not redraw the caret of a field inside). Its height is its content's,
//   up to 8 px under the top, and it scrolls inside.
// - Three parts: the title, what it holds, and a close button at the bottom
//   (above the home bar).
// - It closes by swiping its grabber or its title down (swipe-down.ts; past
//   half its height or on a flick, as the old web's), its close button, the
//   scrim, Escape, and the browser's or the OS's back (no page move). Up, the focus goes to the drawer, never into
//   a field (no keyboard by itself); closing with the focus inside gives it
//   back to what opened it.
// - Where a host draws the Dock (a native app), the host's Dock, post box and
//   top bar step aside while it is up (`stow`).
// - Moves take 200 ms (none with reduced motion). Down, it stays drawn,
//   hidden and inert, while its owner keeps it (what is typed in it stays).
import { useRouter } from "@tanstack/react-router";
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { isImeComposing } from "../ui/ime";
import { usePhoneState } from "./context";
import { isOverlayEntry, leaveOverlayEntry, pushOverlayEntry } from "./history";
import { useSwipeDown } from "./swipe-down";

/** Pulled down slowly, it closes past this share of its height (as the old web). */
const DRAG_CLOSE_RATIO = 0.5;

export function BottomDrawer({
  open,
  label,
  closeLabel,
  onClose,
  opener,
  children,
}: {
  open: boolean;
  label: string;
  closeLabel: string;
  onClose(): void;
  /** What opened it (the focus goes back there). */
  opener: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const router = useRouter();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const swipe = useSwipeDown(
    () => close.current(),
    () => box.current,
    DRAG_CLOSE_RATIO,
  );
  const dragging = swipe.dragY > 0;

  // The browser's back closes it; closing by other means leaves the entry.
  // frontend-development: not a fetch; the history entry of an overlay.
  useEffect(() => {
    if (!open) return;
    const history = router.history;
    pushOverlayEntry(history);
    let up = true;
    const off = history.subscribe(() => {
      if (up && !isOverlayEntry(history)) close.current();
    });
    return () => {
      up = false;
      off();
      leaveOverlayEntry(history);
    };
  }, [open, router]);

  // Where a host draws the Dock (a native app), its Dock, post box and top
  // bar lie over the page: while open they step aside (the drawer covers the
  // screen). The web's own lie under the scrim and stay as they are.
  const phone = usePhoneState();
  const stow = phone?.hostDraws ? phone.stow : undefined;
  // frontend-development: not a fetch; tells the frame the drawer is up.
  useLayoutEffect(() => (open && stow ? stow() : undefined), [open, stow]);

  // frontend-development: not a fetch; where the focus goes on open and close.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    if (open) {
      if (!el.contains(document.activeElement))
        el.focus({ preventScroll: true });
    } else if (el.contains(document.activeElement))
      opener.current?.focus({ preventScroll: true });
  }, [open, opener]);

  return (
    <div
      data-bottom-drawer-layer=""
      className={`absolute inset-0 z-50 ${open ? "" : "pointer-events-none"}`}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => close.current()}
        className={`absolute inset-0 cursor-default bg-black/30 transition-opacity duration-200 ease-out motion-reduce:transition-none starting:opacity-0 ${open ? "opacity-100" : "opacity-0"}`}
      />
      <div
        ref={box}
        role="dialog"
        aria-label={label}
        aria-hidden={!open || undefined}
        inert={!open || undefined}
        tabIndex={-1}
        data-bottom-drawer=""
        onKeyDown={(e) => {
          if (
            e.key !== "Escape" ||
            e.defaultPrevented ||
            isImeComposing(e.nativeEvent)
          )
            return;
          e.preventDefault();
          close.current();
        }}
        // Swiped, it follows the finger with no move; released, it moves on
        // from there (the same `translate` as its classes, so no jump).
        style={dragging ? { translate: `0 ${swipe.dragY}px` } : undefined}
        // Going up it is visible at once (the focus moves in at the start);
        // going down the visibility follows the move.
        className={`absolute inset-x-0 bottom-0 flex max-h-[calc(100%-8px)] flex-col overflow-hidden rounded-t-[20px] bg-surface text-fg shadow-[var(--shadow-sheet)] outline-none duration-200 ease-out starting:translate-y-full motion-reduce:transition-none ${open ? "translate-y-0" : "invisible translate-y-full"} ${dragging ? "transition-none" : open ? "transition-[translate]" : "transition-[translate,visibility]"}`}
      >
        {/* The grabber and the title: swiping down here closes (the vertical
            move is not the browser's). */}
        <div
          data-bottom-drawer-grab=""
          className="flex-none touch-none select-none"
          {...swipe.handlers}
        >
          <div aria-hidden="true" className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-10 rounded-full bg-border" />
          </div>
          <div className="truncate border-b border-border-soft px-5 pt-1 pb-3 text-[15px]">
            {label}
          </div>
        </div>
        <div className="min-h-0 flex-auto overflow-y-auto overscroll-contain px-3 pt-3 pb-3">
          {children}
        </div>
        <div className="flex-none border-t border-border-soft px-5 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => close.current()}
            className="flex min-h-11 w-full cursor-pointer items-center justify-center rounded-[10px] px-3 text-[13px] text-body hover:bg-hover active:bg-hover"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
